import { useEffect, useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { getPlans, PLANS, currencySymbol, intervalSuffix } from '../../config/plans';
import { BRAND_DEFAULTS } from '../../config/landing';
import defaultLogo from '../../assets/landing/flocktrade-logo.png';

/*
 * Landing data hooks.
 *
 * useLandingBrand  — resolves brand name / wordmark / logo / accent from the
 *                    active tenant theme, falling back to FlockTrade defaults.
 * useLandingPlans  — returns the canonical plan list, overlaid with a
 *                    white-label tenant's own plans (GET /api/tenant/plans,
 *                    the same endpoint the in-app /pricing page reads) so the
 *                    landing never hardcodes prices that can diverge.
 */

export function useLandingBrand() {
  const { tenantConfig } = useTheme();
  const isCustomTenant = Boolean(tenantConfig && tenantConfig.slug && tenantConfig.slug !== 'flocktrade');

  const name = tenantConfig?.name || BRAND_DEFAULTS.name;
  return {
    name,
    wordmark: isCustomTenant ? name.toUpperCase() : BRAND_DEFAULTS.wordmark,
    logo: (isCustomTenant && tenantConfig?.logo) || defaultLogo,
    accent: (isCustomTenant && tenantConfig?.colors?.primary) || null,
    isCustomTenant,
  };
}

// Map a tenant plan object (from /api/tenant/plans) onto the landing card shape.
function mapTenantPlan(tp, idx) {
  const hasPrice = typeof tp.price === 'number';
  return {
    key: tp.key || `plan-${idx}`,
    name: tp.name,
    price: hasPrice ? tp.price : undefined,
    priceLabel: hasPrice ? undefined : 'Custom',
    currency: tp.currency || 'usd',
    interval: tp.interval || 'month',
    tagline: tp.tagline || '',
    features: Array.isArray(tp.features) ? tp.features : [],
    highlightLabel: tp.highlightLabel || '',
    featured: Boolean(tp.highlightLabel),
    cta: 'Start Free Trial',
    ctaHref: `/signup?plan=${encodeURIComponent(tp.key || '')}`,
    ctaVariant: tp.highlightLabel ? 'primary' : 'ghost',
  };
}

export function useLandingPlans() {
  // Plans are managed in tenants.plans (admin → Membership Plans) for every
  // tenant, FlockTrade included. Fetch the live, visible plans; src/config/plans.js
  // is only the fallback when none are configured. Enterprise is a non-purchasable
  // "Contact Sales" card that lives in config, so graft it on when the DB plans
  // don't already include it.
  const [plans, setPlans] = useState(() => getPlans());

  useEffect(() => {
    let cancelled = false;
    fetch('/api/tenant/plans')
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        if (cancelled) return;
        if (Array.isArray(data) && data.length > 0) {
          const mapped = data.map(mapTenantPlan);
          if (!mapped.some((p) => p.key === 'enterprise') && PLANS.enterprise) {
            mapped.push({ ...PLANS.enterprise });
          }
          setPlans(mapped);
        } else {
          setPlans(getPlans());
        }
      })
      .catch(() => { if (!cancelled) setPlans(getPlans()); });
    return () => { cancelled = true; };
  }, []);

  return plans;
}

// Render a plan's headline price as parts the card can style (currency / amount / period).
export function planPriceParts(plan) {
  if (typeof plan.price !== 'number') {
    return { custom: plan.priceLabel || 'Custom' };
  }
  return {
    currency: currencySymbol(plan.currency),
    amount: String(plan.price),
    period: intervalSuffix(plan.interval),
  };
}
